import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { tierMeta, pathMatches, type AlertTier } from "@/lib/alertStyles";
import { cn } from "@/lib/utils";

interface AlertRow {
  id: string;
  tier: AlertTier;
  title: string;
  description: string;
  app_wide: boolean;
  page_keys: string[];
  active: boolean;
  created_at: string;
}

interface PageRow {
  key: string;
  path_pattern: string;
}

const DISMISS_KEY = "barka.dismissedAlerts";

function getDismissed(): string[] {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]");
  } catch {
    return [];
  }
}

export function SystemAlertBanner() {
  const location = useLocation();
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [pages, setPages] = useState<PageRow[]>([]);
  const [dismissed, setDismissed] = useState<string[]>(getDismissed());

  useEffect(() => {
    const load = async () => {
      const [{ data: a }, { data: p }] = await Promise.all([
        supabase.from("system_alerts" as any).select("*").eq("active", true),
        supabase.from("app_pages" as any).select("key, path_pattern"),
      ]);
      if (a) setAlerts(a as any);
      if (p) setPages(p as any);
    };
    load();

    const channel = supabase
      .channel("system_alerts_banner")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "system_alerts" },
        () => load(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
  };

  const visible = alerts
    .filter((a) => {
      if (dismissed.includes(a.id)) return false;
      if (a.app_wide) return true;
      return a.page_keys.some((key) => {
        const page = pages.find((p) => p.key === key);
        return page && pathMatches(page.path_pattern, location.pathname);
      });
    })
    // Oldest first; rendered in reverse so oldest sits at the bottom of the stack.
    .sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );

  if (visible.length === 0) return null;

  return (
    <div
      className={cn(
        "fixed z-[1000] flex flex-col-reverse gap-2 pointer-events-none",
        // Mobile: full width across bottom, slightly above edge
        "left-2 right-2 bottom-4",
        // Desktop: anchored bottom-right, constrained width
        "sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm sm:w-[22rem]",
      )}
      style={{
        // Keep above iOS keyboard / safe area without shifting on focus
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      role="region"
      aria-label="Systemmeddelanden"
    >
      {visible.map((a) => {
        const meta = tierMeta[a.tier];
        const Icon = meta.icon;
        return (
          <div
            key={a.id}
            className={cn(
              "pointer-events-auto rounded-2xl border shadow-card-hover backdrop-blur-md",
              "px-4 py-3 animate-fade-in",
              meta.banner,
            )}
            role="alert"
          >
            <div className="flex items-start gap-3">
              <Icon className={cn("h-5 w-5 mt-0.5 shrink-0", meta.iconColor)} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight">{a.title}</p>
                {a.description && (
                  <p className="text-sm leading-snug opacity-90 mt-0.5 break-words">
                    {a.description}
                  </p>
                )}
              </div>
              <button
                onClick={() => dismiss(a.id)}
                className="shrink-0 rounded-full p-1 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                aria-label="Stäng"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
